import { useEffect, useMemo, useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { useCart } from "../store/cartStore";
import { supabase } from "../lib/supabase";
import { fmtK } from "../lib/money";
import {
  PAYMENT_METHODS,
  DELIVERY_METHODS,
  EMPTY_ADDR_FORM,
  addressHint,
} from "./checkout/constants";
import { Label, RadioRow, Row, inputStyle } from "./checkout/uiHelpers";

// Checkout flow — step 1 address, step 2 payment, step 3 confirm.
// Mirrors mobile/src/services/checkoutService.ts (the dev/direct-payment path):
// validate stock → create parent order → sub_orders (status=confirmed,
// payment_status=paid) → order_items → decrement_stock RPC → escrow_ledger →
// payments → notifications → increment_seller_orders. When real Kazang/DPO
// payments go live (Step 15 in CLAUDE.md), stock decrement moves to the
// webhook and sub_orders start in pending_payment.

export function Checkout({ user, onClose, onComplete }) {
  const items = useCart((s) => s.items);
  const clear = useCart((s) => s.clear);
  const [step, setStep] = useState(1);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addrId, setAddrId] = useState(null);
  const [addrForm, setAddrForm] = useState(EMPTY_ADDR_FORM);
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrErr, setAddrErr] = useState(null);
  const [payment, setPayment] = useState("mtn");
  const [delivery, setDelivery] = useState("courier");
  const [sellerMeta, setSellerMeta] = useState({});
  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState(null);

  const sellerIds = useMemo(() => [...new Set(items.map((i) => i.seller_id).filter(Boolean))], [items]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    async function load() {
      const [addrRes, sellerRes] = await Promise.all([
        supabase
          .from("delivery_addresses")
          .select("*")
          .eq("user_id", user.id)
          .order("is_default", { ascending: false })
          .limit(20),
        sellerIds.length
          ? supabase.from("sellers").select("id, store_name, delivery_fee").in("id", sellerIds)
          : Promise.resolve({ data: [] }),
      ]);
      if (cancelled) return;
      const addrs = addrRes.data || [];
      setSavedAddresses(addrs);
      const def = addrs.find((a) => a.is_default) || addrs[0];
      setAddrId(def ? def.id : "new");
      const map = {};
      (sellerRes.data || []).forEach((s) => (map[s.id] = s));
      setSellerMeta(map);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, sellerIds.join(",")]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const i of items) {
      const k = i.seller_id || "unknown";
      if (!m.has(k)) m.set(k, { seller_id: i.seller_id, seller_name: i.seller_name, items: [] });
      m.get(k).items.push(i);
    }
    return Array.from(m.values());
  }, [items]);

  const subtotal = items.reduce((s, i) => s + (i.price || 0) * (i.qty || 0), 0);
  const deliveryTotal = groups.reduce((s, g) => s + (sellerMeta[g.seller_id]?.delivery_fee || 0), 0);
  const total = subtotal + deliveryTotal;

  const activeAddress = savedAddresses.find((a) => a.id === addrId);
  const newAddressReady =
    addrForm.label.trim() && addrForm.recipient_name.trim() && addrForm.street_address.trim();
  const addressOk =
    delivery !== "courier" ||
    (addrId === "new" ? !!newAddressReady : !!activeAddress);

  async function saveNewAddress() {
    if (!user?.id || !newAddressReady) return null;
    setAddrSaving(true);
    setAddrErr(null);
    const isFirst = savedAddresses.length === 0;
    const { data, error } = await supabase
      .from("delivery_addresses")
      .insert({
        user_id: user.id,
        label: addrForm.label.trim(),
        recipient_name: addrForm.recipient_name.trim(),
        phone: addrForm.phone.trim(),
        street_address: addrForm.street_address.trim(),
        city: addrForm.city.trim() || "Lusaka",
        is_default: isFirst,
      })
      .select()
      .single();
    setAddrSaving(false);
    if (error) {
      setAddrErr(error.message || "Couldn't save address.");
      return null;
    }
    setSavedAddresses((prev) => [...prev, data]);
    setAddrId(data.id);
    setAddrForm(EMPTY_ADDR_FORM);
    return data;
  }

  async function handleContinue() {
    // Step 1 → 2: if courier + new address, persist it first so it gets an id.
    if (step === 1 && delivery === "courier" && addrId === "new") {
      const saved = await saveNewAddress();
      if (!saved) return; // error displayed
    }
    setStep(step + 1);
  }

  async function place() {
    if (!user?.id) return;
    setPlacing(true);
    setErr(null);
    try {
      // 0. Stock validation (batched).
      const productIds = [...new Set(items.map((i) => i.product_id).filter(Boolean))];
      if (productIds.length > 0) {
        const { data: stockData } = await supabase
          .from("products")
          .select("id, title, stock_qty")
          .in("id", productIds)
          .limit(50);
        const stockMap = {};
        (stockData || []).forEach((p) => (stockMap[p.id] = p));
        for (const it of items) {
          const prod = stockMap[it.product_id];
          if (!prod) {
            // Product couldn't be fetched — deleted, deactivated, or no longer
            // visible. Surface a clear, actionable error.
            throw new Error(
              `"${it.title || "An item in your cart"}" is no longer available. Please remove it from your cart to continue.`
            );
          }
          if (prod.stock_qty !== null && prod.stock_qty < it.qty) {
            throw new Error(
              `Insufficient stock for "${prod.title || "product"}". Only ${prod.stock_qty} left.`
            );
          }
        }
      }

      // 1. Order number + parent order. The RPC uses a DB sequence so
      //    collisions should be impossible, but we retry once on 23505
      //    (unique_violation) in case a stale/legacy client produced the same
      //    number concurrently.
      const courierAddr = delivery === "courier" ? activeAddress : null;
      let orderNum = null;
      let order = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data: nextNum, error: orderNumErr } = await supabase.rpc(
          "generate_order_number"
        );
        if (orderNumErr)
          throw new Error(`Failed to generate order number: ${orderNumErr.message}`);
        orderNum = nextNum;

        const { data: inserted, error: orderErr } = await supabase
          .from("orders")
          .insert({
            buyer_id: user.id,
            order_number: orderNum,
            total_amount: total,
            sub_order_count: groups.length,
            status: "pending",
            delivery_address_id: courierAddr?.id || null,
          })
          .select()
          .single();

        if (!orderErr) {
          order = inserted;
          break;
        }
        if (orderErr?.code === "23505" && attempt < 2) continue;
        throw orderErr;
      }
      if (!order) throw new Error("Couldn't place order — please try again.");

      // 3. Per-seller sub_orders + items + escrow + payment + notifications.
      const courierAddrText = courierAddr
        ? [courierAddr.recipient_name, courierAddr.street_address, courierAddr.city]
            .filter(Boolean)
            .join(", ")
        : null;

      for (let i = 0; i < groups.length; i++) {
        const g = groups[i];
        const meta = sellerMeta[g.seller_id];
        const gSubtotal = g.items.reduce((s, it) => s + (it.price || 0) * (it.qty || 0), 0);
        const gDelivery = meta?.delivery_fee || 0;
        const gTotal = gSubtotal + gDelivery;
        const subLetter = String.fromCharCode(65 + i); // A, B, C…

        const { data: sub, error: subErr } = await supabase
          .from("sub_orders")
          .insert({
            order_id: order.id,
            seller_id: g.seller_id,
            sub_number: `${orderNum}-${subLetter}`,
            subtotal: gSubtotal,
            delivery_fee: gDelivery,
            total: gTotal,
            payment_method: payment,
            delivery_method: delivery,
            delivery_address: delivery === "courier" ? courierAddrText : null,
            status: "confirmed",
            payment_status: "paid",
            payment_seq: i + 1,
            confirmed_at: new Date().toISOString(),
          })
          .select()
          .single();
        if (subErr) throw subErr;

        // Order items with product snapshot (name + first image).
        const itemRows = g.items.map((it) => {
          const imgs = it.images;
          const firstImg = Array.isArray(imgs) && imgs[0];
          const imageSnap = firstImg
            ? typeof firstImg === "string"
              ? firstImg
              : firstImg.thumb || firstImg.med || firstImg.full || null
            : null;
          return {
            sub_order_id: sub.id,
            product_id: it.product_id,
            quantity: it.qty,
            unit_price: it.price,
            total: it.price * it.qty,
            size: it.size || null,
            product_name: it.title || null,
            product_image: imageSnap,
          };
        });
        const { error: itemsErr } = await supabase.from("order_items").insert(itemRows);
        if (itemsErr) throw itemsErr;

        // Stock decrement — race-safe RPC.
        const decs = await Promise.all(
          g.items
            .filter((it) => it.product_id)
            .map((it) =>
              supabase.rpc("decrement_stock", { p_product_id: it.product_id, p_qty: it.qty })
            )
        );
        const decErr = decs.find((d) => d.error);
        if (decErr) console.warn("decrement_stock failed:", decErr.error);

        // Escrow ledger (48h hold from now — matches mobile direct-pay path).
        const commissionRate = 10;
        const commissionAmount = Math.round((gTotal * commissionRate) / 100);
        await supabase.from("escrow_ledger").insert({
          sub_order_id: sub.id,
          seller_id: g.seller_id,
          amount: gTotal,
          commission_amount: commissionAmount,
          commission_rate: commissionRate,
          seller_payout: gTotal - commissionAmount,
          status: "held",
          hold_until: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
        });

        // Payment record (audit trail — simulated success on this path).
        await supabase.from("payments").insert({
          sub_order_id: sub.id,
          provider: payment,
          provider_ref: `SIM-${Date.now()}-${i}`,
          amount: gTotal,
          status: "success",
          idempotency_key: `pay-${orderNum}-${subLetter}-${Date.now()}`,
          completed_at: new Date().toISOString(),
        });

        // Seller notification.
        const { data: sellerRow } = await supabase
          .from("sellers")
          .select("user_id")
          .eq("id", g.seller_id)
          .maybeSingle();
        if (sellerRow) {
          await supabase.from("notifications").insert({
            user_id: sellerRow.user_id,
            type: "order",
            title: "New Order!",
            body: `New order ${orderNum}-${subLetter}: ${g.items.length} item(s) — ${fmtK(gTotal)}`,
            channel: "in_app",
            data: { order_id: order.id, sub_order_id: sub.id },
          });
        }

        // Seller stats (best-effort).
        await supabase
          .rpc("increment_seller_orders", { p_seller_id: g.seller_id, p_revenue: gTotal })
          .then(null, () => {});
      }

      // 4. Buyer notification.
      await supabase.from("notifications").insert({
        user_id: user.id,
        type: "order",
        title: "Order Confirmed!",
        body: `Your order ${orderNum} has been confirmed. ${groups.length} seller(s) are preparing your items.`,
        channel: "in_app",
        data: { order_id: order.id },
      });

      clear();
      onComplete?.(order.id, orderNum);
    } catch (e) {
      // Translate raw UUIDs in backend errors (e.g. "Insufficient stock for
      // product <uuid>") into "<title>" so the user can identify the item.
      const rawMsg = e.message || "Couldn't place order — please try again.";
      const uuidRegex =
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
      const titleByPid = {};
      for (const it of items) {
        if (it.product_id && it.title) titleByPid[it.product_id] = it.title;
      }
      const friendlyMsg = rawMsg.replace(uuidRegex, (uuid) => {
        const title = titleByPid[uuid];
        return title ? `"${title}"` : "an item in your cart";
      });
      setErr(friendlyMsg);
    } finally {
      setPlacing(false);
    }
  }

  const stepLabel = ["Address", "Payment", "Review"][step - 1];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.45)",
        zIndex: 200,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "60px 24px",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 100%)",
          background: C.bg,
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            padding: "20px 26px",
            background: C.white,
            borderBottom: "1px solid rgba(196,193,183,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <Eyebrow>Checkout · Step {step}/3</Eyebrow>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.charcoal }}>{stepLabel}</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
          >
            <Icon name="x" size={20} color={C.charcoal} />
          </button>
        </div>

        <div style={{ display: "flex", gap: 4, padding: "0 26px", marginTop: 12 }}>
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: n <= step ? C.hunter : C.dustDk,
              }}
            />
          ))}
        </div>

        <div style={{ padding: 26, maxHeight: "60vh", overflowY: "auto" }}>
          {step === 1 && (
            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <Label>Delivery method</Label>
                <div style={{ display: "grid", gap: 8 }}>
                  {DELIVERY_METHODS.map((m) => (
                    <RadioRow
                      key={m.id}
                      label={m.label}
                      hint={m.hint}
                      selected={delivery === m.id}
                      onClick={() => setDelivery(m.id)}
                    />
                  ))}
                </div>
              </div>

              {delivery === "courier" && (
                <div>
                  <Label>Delivery address</Label>
                  <div style={{ display: "grid", gap: 8 }}>
                    {savedAddresses.map((a) => (
                      <RadioRow
                        key={a.id}
                        label={a.label || "Address"}
                        hint={addressHint(a)}
                        selected={addrId === a.id}
                        onClick={() => setAddrId(a.id)}
                      />
                    ))}
                    <RadioRow
                      label="New address"
                      hint="Enter a delivery address"
                      selected={addrId === "new"}
                      onClick={() => setAddrId("new")}
                    />
                    {addrId === "new" && (
                      <div style={{ display: "grid", gap: 8, marginTop: 2 }}>
                        <input
                          value={addrForm.label}
                          onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })}
                          placeholder="Label (e.g. Home, Dorm, Office)"
                          style={inputStyle}
                        />
                        <input
                          value={addrForm.recipient_name}
                          onChange={(e) =>
                            setAddrForm({ ...addrForm, recipient_name: e.target.value })
                          }
                          placeholder="Recipient full name"
                          style={inputStyle}
                        />
                        <input
                          value={addrForm.phone}
                          onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })}
                          placeholder="Delivery phone"
                          style={inputStyle}
                        />
                        <textarea
                          value={addrForm.street_address}
                          onChange={(e) =>
                            setAddrForm({ ...addrForm, street_address: e.target.value })
                          }
                          placeholder="Street, neighbourhood, landmark…"
                          rows={2}
                          style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
                        />
                        <input
                          value={addrForm.city}
                          onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })}
                          placeholder="City (default: Lusaka)"
                          style={inputStyle}
                        />
                        {addrErr && (
                          <div
                            style={{
                              fontSize: 12,
                              padding: "8px 10px",
                              background: C.redBg,
                              color: C.red,
                              borderRadius: 8,
                              fontWeight: 600,
                            }}
                          >
                            {addrErr}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <Label>Payment method</Label>
              <div style={{ display: "grid", gap: 8 }}>
                {PAYMENT_METHODS.map((m) => (
                  <RadioRow
                    key={m.id}
                    icon={m.icon}
                    label={m.label}
                    hint={m.hint}
                    selected={payment === m.id}
                    onClick={() => setPayment(m.id)}
                  />
                ))}
              </div>
              <div
                style={{
                  marginTop: 14,
                  fontSize: 11.5,
                  color: C.muted,
                  padding: 10,
                  background: C.cream,
                  borderRadius: 8,
                }}
              >
                Your payment is held in escrow until you confirm delivery. Commission is 10% per sub-order.
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ display: "grid", gap: 14 }}>
              <Card pad={14}>
                <Eyebrow>Items</Eyebrow>
                <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                  {groups.map((g) => (
                    <div key={g.seller_id}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: C.charcoal, marginBottom: 4 }}>
                        {sellerMeta[g.seller_id]?.store_name || g.seller_name || "Seller"}
                      </div>
                      {g.items.map((it) => (
                        <div
                          key={it.product_id + (it.size || "")}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 12,
                            color: C.slate,
                          }}
                        >
                          <span>
                            {it.title} × {it.qty}
                            {it.size ? ` · ${it.size}` : ""}
                          </span>
                          <span style={{ fontWeight: 700, color: C.charcoal }}>{fmtK(it.price * it.qty)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </Card>

              <Card pad={14}>
                <Eyebrow>Where & how</Eyebrow>
                <div style={{ fontSize: 12.5, color: C.charcoal, fontWeight: 700, marginTop: 6 }}>
                  {DELIVERY_METHODS.find((d) => d.id === delivery)?.label}
                </div>
                {delivery === "courier" && activeAddress && (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                    {addressHint(activeAddress)}
                  </div>
                )}
                <div style={{ fontSize: 12.5, color: C.charcoal, fontWeight: 700, marginTop: 10 }}>
                  {PAYMENT_METHODS.find((p) => p.id === payment)?.label}
                </div>
              </Card>

              <Card pad={14}>
                <Row label="Subtotal" value={fmtK(subtotal)} />
                <Row
                  label={`Delivery (${groups.length} seller${groups.length === 1 ? "" : "s"})`}
                  value={deliveryTotal > 0 ? fmtK(deliveryTotal) : "Free"}
                />
                <div style={{ height: 1, background: C.cream, margin: "8px 0" }} />
                <Row label="Total" value={fmtK(total)} bold />
              </Card>

              {err && (
                <div
                  style={{
                    padding: 12,
                    background: C.redBg,
                    borderRadius: 10,
                    border: `1px solid ${C.red}33`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: C.red,
                      fontWeight: 700,
                      marginBottom: 10,
                    }}
                  >
                    {err}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn
                      kind="outline"
                      onClick={() => {
                        setErr(null);
                        onClose?.();
                      }}
                      disabled={placing}
                    >
                      Back to Cart
                    </Btn>
                    <Btn
                      onClick={() => {
                        setErr(null);
                        place();
                      }}
                      disabled={placing}
                    >
                      Try Again
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            padding: "16px 26px",
            background: C.white,
            borderTop: "1px solid rgba(196,193,183,0.3)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>Order total</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.hunter }}>{fmtK(total)}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 1 && (
              <Btn kind="outline" onClick={() => setStep(step - 1)} disabled={placing}>
                Back
              </Btn>
            )}
            {step < 3 ? (
              <Btn onClick={handleContinue} disabled={!addressOk || addrSaving}>
                {addrSaving ? "Saving…" : "Continue"}
              </Btn>
            ) : (
              <Btn onClick={place} disabled={placing} icon={placing ? undefined : "lock"}>
                {placing ? "Placing…" : "Place order"}
              </Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

